import CustomQuickFilterBox from '@components/ag-grid/CustomQuickFilterBox';
import { Loading } from '@components/atomic/atoms';
import { ConfirmModal } from '@components/atomic/organisms';
import CustomPaginationMui from '@components/materialComponents/CustomPaginationMui';
import { OETrans } from '@components/translation/OETrans';
import { translate } from '@components/translation/translation';
import { deleteDocumentsStaging, getDocumentsStaging } from '@lib/api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import React, { useEffect, useState } from 'react';
import { Button, ButtonToolbar, Col, Row } from 'react-bootstrap';

interface DocumentsStagingTableProps {
	entityType: string;
	projectId: string;
	subActiveKey: string;
	localeText: any;
	columnDefs: ColDef[];
	defaultColDef: ColDef;
	gridRef: React.RefObject<AgGridReact>;
	onGridReady: (params: any, gridRef: React.RefObject<AgGridReact>) => void;
	sbShow: React.ReactNode;
	batchState: string;
	setSbShow: React.Dispatch<React.SetStateAction<React.ReactNode>>;
	handleStatusBar: (type: 'success' | 'error', message: string, autoClose: boolean) => void;
	onPageSizeChanged: (refTable: string, gridRef: React.RefObject<AgGridReact>) => void;
	optionsPagination: number[];
}

const DocumentsStagingTable: React.FC<DocumentsStagingTableProps> = ({
	entityType,
	projectId,
	subActiveKey,
	localeText,
	columnDefs,
	defaultColDef,
	gridRef,
	onGridReady,
	sbShow,
	setSbShow,
	batchState,
	handleStatusBar,
	onPageSizeChanged,
	optionsPagination,
}) => {
	const queryClient = useQueryClient();
	const [documentsStaging, setDocumentsStaging] = useState<any[]>();
	const [documentsStagingMeta, setDocumentsStagingMeta] = useState<any>({});
	const [isFirstResearch4, setIsFirstResearch4] = useState(true);
	const [isRefreshing, setIsRefreshing] = useState(false);
	const gridRefDocumentsStaging = React.useRef<AgGridReact>(null);

	const { data: stagingDocumentsQuery } = useQuery({
		queryKey: ['staging-docs', entityType, projectId],
		queryFn: () => {
			let docEntityType = 'projects';
			let docTaskEntity = entityType;

			return getDocumentsStaging(docEntityType, docTaskEntity, projectId, entityType + 'DocStaging');
		},
		enabled: subActiveKey === 'log-documents' && !!projectId,
		refetchInterval: batchState === 'DOING' ? 5000 : false,
	});

	useEffect(() => {
		if (stagingDocumentsQuery?.data) {
			setDocumentsStaging(stagingDocumentsQuery.data);
			setDocumentsStagingMeta({ meta: stagingDocumentsQuery.meta });
		}
	}, [stagingDocumentsQuery]);

	const [selectedDocumentsStaging, setSelectedDocumentsStaging] = useState([]);
	const [showConfirmModal, setShowConfirmModal] = useState(false);

	const onSelectionChanged = (e) => {
		//Prende solo le righe selezionate e filtrate
		let rowData = [];
		e.api.forEachNode((node) => (node.isSelected() ? rowData.push(node.data) : null));

		const selectedData = rowData.map((node) => node);

		setSelectedDocumentsStaging(selectedData.map((el) => el.id));
	};

	const cleanData = async () => {
		setSbShow(<></>);

		const data = {
			docIds: selectedDocumentsStaging,
		};

		const result = await deleteDocumentsStaging(data, 'projects', entityType, projectId);

		if (result.success) {
			// pollDataBatch();
			queryClient.invalidateQueries({ queryKey: ['staging-docs', entityType, projectId] });

			handleStatusBar('success', translate('common.message.logsdeleted', 'Log eliminati con successo'), true);
		} else {
			handleStatusBar(
				'error',
				result.message ?? translate('common.error.save', 'Si è verificato un errore durante il salvataggio delle informazioni'),
				false,
			);
		}
	};

	const CleanButton = () => {
		return (
			<>
				<ConfirmModal
					title={<OETrans code="common.cleanlog.confirmTitle" fallback={'Elimina i log dei caricamenti effettuati'} />}
					message={
						<OETrans
							code="common.cleanlog.confirmText"
							fallback={
								"Stai effettuando l'eliminazione dei log di caricamento dati. Questa operazione eliminerà in modo permanente le informazioni. Sei sicuro/a di voler procedere"
							}
						/>
					}
					show={showConfirmModal}
					callback={cleanData}
					onHide={() => setShowConfirmModal(false)}
					confirmButtonLabel="Conferma"
				/>
				<Button variant="danger" className="me-2" onClick={() => setShowConfirmModal(true)}>
					<OETrans code="common.cleanlog" fallback={'Elimina Log'} />
				</Button>
			</>
		);
	};

	return (
		<>
			{documentsStaging?.length > 0 ? (
				<>
					<Row className="align-items-center mb-4">
						<Col className="col-4">
							<CustomQuickFilterBox
								tableRef={gridRefDocumentsStaging}
								dataMeta={documentsStagingMeta}
								setData={setDocumentsStaging}
								dataLength={documentsStaging?.length}
								isFirstResearch={isFirstResearch4}
								setIsFirstResearch={setIsFirstResearch4}
								refTable={entityType + 'DocStaging'}
								isRefreshing={isRefreshing}
								setIsRefreshing={setIsRefreshing}
								isQuickFilterDisabled={false}
								setDataMeta={setDocumentsStagingMeta}
							/>
						</Col>
						<Col className="col-8 text-right py-1">
							<ButtonToolbar className="justify-content-end">{selectedDocumentsStaging.length > 0 && <CleanButton />}</ButtonToolbar>
						</Col>
					</Row>

					<Row>
						<Col className="col-12">
							{Array.isArray(documentsStaging) ? (
								<div className="ag-theme-alpine list-table" id="grid-wrapper">
									<AgGridReact
										ref={gridRefDocumentsStaging}
										columnDefs={columnDefs}
										rowData={documentsStaging}
										defaultColDef={defaultColDef}
										domLayout="autoHeight"
										tooltipShowDelay={0}
										tooltipHideDelay={2000}
										tooltipMouseTrack={true}
										onGridReady={(e) => onGridReady(e, gridRefDocumentsStaging)}
										headerHeight={50}
										rowHeight={50}
										suppressCellFocus={true}
										animateRows={true}
										localeText={localeText}
										rowSelection={'multiple'}
										onSelectionChanged={onSelectionChanged}
										suppressRowClickSelection={true}
										rowMultiSelectWithClick={true}
									/>
									{Object.keys(documentsStagingMeta).length > 0 ? (
										<CustomPaginationMui
											dataMeta={documentsStagingMeta}
											setData={setDocumentsStaging}
											setDataMeta={setDocumentsStagingMeta}
											refTable={entityType + 'DocStaging'}
											setIsFirstResearch={setIsFirstResearch4}
											setIsChangedPagination={() => {}}
										/>
									) : (
										<div className="page-size-table">
											<OETrans code="common.itemsperpage" fallback={'Elementi per pagina'} />
											:&nbsp;
											<select
												onChange={() => onPageSizeChanged('log-documents', gridRefDocumentsStaging)}
												className="page-size"
												id="page-size-log-documents"
											>
												{optionsPagination}
											</select>
										</div>
									)}
								</div>
							) : (
								<Loading />
							)}
						</Col>
					</Row>
				</>
			) : (
				<Row className="text-center my-5">
					<span>
						<OETrans code="common.nologs" fallback={'Non sono presenti log'} />
					</span>
				</Row>
			)}
		</>
	);
};

export default DocumentsStagingTable;
